//////////////////
// IMPORTS
//////////////////
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
// suggestion-action-autopilot imports
import { IContainer, IProps, IState } from "./SuggestionActionAutopilot.d";
import { fragments } from "./SuggestionActionAutopilot.gql";
import * as s from "./SuggestionActionAutopilot.styles";
import { CardButton } from "@components/card";
// other components
import {
  AutopilotFormFlex,
  AutopilotFormRoom,
  AutopilotFormLearnMore,
} from "@components/autopilot-form";
// state
import { IReduxState } from "@reducers/root.reducer";
import { addAndRemoveOrgTags } from "@mutations";
import { snackBarOpen } from "@actions/snack-bar.actions";
// util
import { generateAutopilotTags, EFlex } from "@util/event-tag.util";
import { createEventParentRelayIdFromOrgRelayId, getBaseExternalEventId } from "@util/event.util";
// material-ui
import { Button } from "@material-ui/core";
import { Assistant, Check, KeyboardArrowDown } from "@material-ui/icons";
import withStyles from "@material-ui/core/styles/withStyles";

//////////////////
// COMPONENT
//////////////////
class SuggestionActionAutopilotCmpt extends React.Component<IProps, IState> {
  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  constructor(props: IProps) {
    super(props);
    this.state = this.getStateOffProps(props);
    this.maybeUpdateParent(this.state.autopilotOptions);
  }

  private getStateOffProps = (props: IProps) => {
    const autopilotOptions = {
      flex: EFlex.Day,
      allowSimilarRooms: props.payload.hasRoom ? false : null,
    };

    return {
      autopilotOptions,
      saving: false,
      optionsExpanded: false,
    };
  };

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private isPinned = () => {
    return this.state.autopilotOptions.flex === EFlex.Pin;
  };

  // ~-~-~-~-~-~-~-
  // Handlers
  // ~-~-~-~-~-~-~-
  private maybeUpdateParent = (autopilotOptions: IState["autopilotOptions"]) => {
    if (this.props.autopilotOptionsUpdate) {
      this.props.autopilotOptionsUpdate(autopilotOptions);
    }
  };

  private onFlexibilityChange = (flex: EFlex) => {
    const autopilotOptions = { ...this.state.autopilotOptions, flex };
    this.setState({ autopilotOptions }, () => this.maybeUpdateParent(autopilotOptions));
  };

  private onRoomChange = (allowSimilarRooms: boolean) => {
    const autopilotOptions = { ...this.state.autopilotOptions, allowSimilarRooms };
    this.setState({ autopilotOptions }, () => this.maybeUpdateParent(autopilotOptions));
  };

  private onClickAutopilot = () => {
    this.saveSettings();
  };

  private onClickDetails = () => {
    this.setState({ optionsExpanded: !this.state.optionsExpanded });
  };

  private maybeRemoveCard = (externalEventId: string) => {
    if (!this.props.actionCallback) {
      return;
    }

    // call card remove with tracking data
    const { flex, allowSimilarRooms } = this.state.autopilotOptions;
    const baseExternalEventId = getBaseExternalEventId(externalEventId);
    this.props.actionCallback({ flex, allowSimilarRooms, externalEventId: baseExternalEventId });
  };

  // ~-~-~-~-~-~-~-
  // Saving
  // ~-~-~-~-~-~-~-
  private saveSettings = () => {
    const { payload, org, relay } = this.props;
    const eventParentRelayId = createEventParentRelayIdFromOrgRelayId(
      org.id,
      payload.externalEventId,
    );
    this.setState({ saving: true });

    // this shouldn't happen
    if (!eventParentRelayId) {
      this.onError();
      return;
    }

    // create tags for saving
    const tags = generateAutopilotTags(this.state.autopilotOptions);

    // add the org id and eventparent id to the input
    const input = {
      eventParentRelayId,
      ...tags,
      orgRelayId: org.id,
    };

    // finally, mutate
    addAndRemoveOrgTags(relay.environment, input, this.onSaveSuccess, this.onError);
    // and maybe remove card
    this.maybeRemoveCard(payload.externalEventId);
  };

  private onSaveSuccess = () => {
    this.setState({ saving: false });
    // TODO (lsanwick): Update autopilot language
    const msg = this.isPinned() ? "Dismissed autopilot suggestion" : "Enabled autopilot";
    this.props.snackBarOpen(msg);
  };

  private onError = () => {
    this.setState({ saving: false });
    this.props.snackBarOpen("Sorry, something went wrong");
  };

  // ~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-
  public render() {
    const { label, payload } = this.props;
    const { autopilotOptions } = this.state;
    const optionsContainerStyle = this.state.optionsExpanded
      ? s.moreOptionsExpanded
      : s.moreOptions;

    return (
      <div style={s.container}>
        <Button
          classes={{
            root: this.props.classes.detailsBtn,
            label: this.props.classes.detailsBtnLabel,
          }}
          onClick={this.onClickDetails}
          fullWidth
        >
          Show event settings
          <KeyboardArrowDown
            style={this.state.optionsExpanded ? s.styles.btnIconExpanded : s.styles.btnIcon}
          />
        </Button>

        <div style={optionsContainerStyle}>
          <AutopilotFormFlex
            onChange={this.onFlexibilityChange}
            selectedFlex={autopilotOptions.flex}
            isDisabled={this.state.saving}
          />
          {payload.hasRoom && (
            <AutopilotFormRoom
              onChange={this.onRoomChange}
              defaultAllowSimilarRooms={autopilotOptions.allowSimilarRooms}
              isDisabled={this.state.saving}
            />
          )}
        </div>
        <div style={s.learnMoreContainer}>
          <AutopilotFormLearnMore />
        </div>
        <div style={s.actionContainer}>
          <CardButton
            flat
            icon={this.isPinned() ? <Check /> : <Assistant />}
            onClick={this.onClickAutopilot}
            label={this.isPinned() ? "Done" : label}
          />
        </div>
      </div>
    );
  }
}

//////////////////
// WITH STYLES
//////////////////
export const SuggestionActionAutopilotStyled = withStyles(s.styles)(SuggestionActionAutopilotCmpt);

//////////////////
// REDUX
//////////////////
function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const SuggestionActionAutopilotRedux = connect(mapStateToProps, {
  snackBarOpen,
})(SuggestionActionAutopilotStyled);

//////////////////
// RELAY
//////////////////
export const SuggestionActionAutopilot = createFragmentContainer<IContainer>(
  SuggestionActionAutopilotRedux,
  fragments,
);
